/* =========================================================================
   Phrase Studios — PDP behavior (v2 2026-05-19)
   - Custom variant picker updates Dawn product-form hidden id
   - Size option: single closed pill row, click '+' to expand horizontally
   - Price + URL + mobile-bar sync on variant change
   - Mobile gallery dots track the visible image
   - Mobile sticky ATC bar reveals once the desktop ATC scrolls past
   - Recommendations fetch (lazy, replaces server fallback)
   ========================================================================= */
(function () {
  'use strict';

  const moneyFormat = window.Shopify && window.Shopify.money_format
    ? window.Shopify.money_format
    : '€{{amount}}';

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const value = (function () {
      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = isNaN(precision) ? 2 : precision;
        thousands = thousands || ',';
        decimal = decimal || '.';
        if (isNaN(number) || number == null) return 0;
        number = (number / 100.0).toFixed(precision);
        const parts = number.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const c = parts[1] ? decimal + parts[1] : '';
        return dollars + c;
      }
      switch (format.match(placeholderRegex)[1]) {
        case 'amount': return formatWithDelimiters(cents, 2);
        case 'amount_no_decimals': return formatWithDelimiters(cents, 0);
        case 'amount_with_comma_separator': return formatWithDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return formatWithDelimiters(cents, 0, '.', ',');
        case 'amount_with_space_separator': return formatWithDelimiters(cents, 2, ' ', ',');
        case 'amount_with_apostrophe_separator': return formatWithDelimiters(cents, 2, "'", '.');
        default: return formatWithDelimiters(cents, 2);
      }
    })();
    return format.replace(placeholderRegex, value);
  }

  class PhrasePDP {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.variants = this.parseVariants();
      if (!this.variants) return;
      this.variantMedia = this.parseVariantMedia();

      this.form = root.querySelector('form[action*="/cart/add"]');
      this.idInput = root.querySelector('[data-pdp-variant-id]');
      this.atcPriceEl = root.querySelector('[data-pdp-atc-price]');
      this.atcLabelEl = root.querySelector('.phrase-pdp__atc-label');
      this.atcButton = root.querySelector('[data-pdp-atc]');
      this.mobilePriceEl = root.querySelector('[data-pdp-mobile-price]');
      this.mobileSizeEl = root.querySelector('[data-pdp-mobile-size]');
      this.mobileLabelEl = root.querySelector('[data-pdp-mobile-label]');
      this.mobileBar = root.querySelector('[data-pdp-mobile-bar]');
      this.mobileCta = root.querySelector('[data-pdp-mobile-cta]');

      this.optionsLength = root.querySelectorAll('[data-pdp-option]').length;
      this.selectedOptions = this.readInitialOptions();

      if (this.idInput) this.idInput.disabled = false;

      this.bindChips();
      this.bindSizeToggle();
      this.bindGallery();
      this.bindZoom();
      this.bindMobileBar();
      this.bindMobileCta();
      this.bindSizeGuide();
      this.fetchRecommendations();
      this.hydrateDeliveryDate();
      this.hydrateFreeShipping();
      this.trackRecentlyViewed();
      this.renderRecentlyViewed();
    }

    parseVariants() {
      const node = this.root.querySelector('[data-pdp-variants-json]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        console.error('[phrase-pdp] could not parse variants JSON', e);
        return null;
      }
    }

    parseVariantMedia() {
      const node = this.root.querySelector('[data-pdp-variant-media-json]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return {};
      }
    }

    readInitialOptions() {
      const options = [];
      this.root.querySelectorAll('[data-pdp-option]').forEach((fs) => {
        const active = fs.querySelector('[data-pdp-chip].is-selected') || fs.querySelector('[data-pdp-chip][aria-pressed="true"]');
        options.push(active ? active.dataset.pdpChipValue : null);
      });
      return options;
    }

    bindChips() {
      this.root.querySelectorAll('[data-pdp-option]').forEach((fs) => {
        const optIndex = parseInt(fs.dataset.pdpOptionIndex, 10);
        const isSize = fs.classList.contains('phrase-pdp__option--size');
        fs.querySelectorAll('[data-pdp-chip]').forEach((chip) => {
          if (chip.hasAttribute('disabled')) return;
          chip.addEventListener('click', () => {
            const value = chip.dataset.pdpChipValue;
            fs.querySelectorAll('[data-pdp-chip]').forEach((c) => {
              c.classList.remove('is-selected');
              c.setAttribute('aria-pressed', 'false');
            });
            chip.classList.add('is-selected');
            chip.setAttribute('aria-pressed', 'true');
            this.selectedOptions[optIndex] = value;
            const legendValue = fs.querySelector('[data-pdp-option-value]');
            if (legendValue) legendValue.textContent = value;
            // Update collapsed-size label
            if (isSize) {
              const collapsedLabel = fs.querySelector('[data-pdp-size-current]');
              if (collapsedLabel) collapsedLabel.textContent = value;
              this.collapseSize(fs);
            }
            this.applyVariant();
          });
        });
      });
    }

    bindSizeToggle() {
      this.root.querySelectorAll('[data-pdp-size-toggle]').forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const fs = toggle.closest('[data-pdp-option]');
          if (!fs) return;
          const choices = fs.querySelector('[data-pdp-option-choices]');
          if (!choices) return;
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          if (expanded) {
            this.collapseSize(fs);
          } else {
            this.expandSize(fs);
          }
        });
      });
    }

    expandSize(fs) {
      const toggle = fs.querySelector('[data-pdp-size-toggle]');
      const choices = fs.querySelector('[data-pdp-option-choices]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      if (choices) choices.hidden = false;
      fs.classList.add('is-expanded');
    }

    collapseSize(fs) {
      const toggle = fs.querySelector('[data-pdp-size-toggle]');
      const choices = fs.querySelector('[data-pdp-option-choices]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (choices) choices.hidden = true;
      fs.classList.remove('is-expanded');
    }

    findMatchingVariant() {
      if (!this.variants) return null;
      return this.variants.find((v) => {
        return this.selectedOptions.every((val, idx) => {
          if (val == null) return true;
          return v.options[idx] === val;
        });
      }) || null;
    }

    applyVariant() {
      const variant = this.findMatchingVariant();
      if (!variant) return;

      if (this.idInput) this.idInput.value = variant.id;

      // Swap the gallery to this variant's featured media (fixes colour change leaving the wrong photo).
      if (this.variantMedia) {
        const mediaPos = this.variantMedia[variant.id];
        if (mediaPos && mediaPos > 0) this.goToMedia(mediaPos - 1);
      }

      if (variant.id && history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }

      const priceFormatted = formatMoney(variant.price, moneyFormat);
      if (this.atcPriceEl) {
        const now = this.atcPriceEl.querySelector('.phrase-pdp__atc-now');
        const was = this.atcPriceEl.querySelector('.phrase-pdp__atc-was');
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const wasFormatted = formatMoney(variant.compare_at_price, moneyFormat);
          if (was) {
            was.textContent = wasFormatted;
          } else {
            this.atcPriceEl.innerHTML = `<s class="phrase-pdp__atc-was">${wasFormatted}</s><span class="phrase-pdp__atc-now">${priceFormatted}</span>`;
          }
          if (now) now.textContent = priceFormatted;
        } else {
          this.atcPriceEl.textContent = priceFormatted;
        }
      }
      if (this.mobilePriceEl) this.mobilePriceEl.textContent = priceFormatted;

      if (this.mobileSizeEl) {
        this.mobileSizeEl.textContent = variant.options[0] || 'One size';
      }

      if (this.atcButton) {
        if (variant.available) {
          this.atcButton.removeAttribute('disabled');
          this.atcButton.removeAttribute('aria-disabled');
          if (this.atcLabelEl) this.atcLabelEl.textContent = 'Add to cart';
          if (this.mobileLabelEl) this.mobileLabelEl.textContent = 'Add';
        } else {
          this.atcButton.setAttribute('disabled', 'disabled');
          this.atcButton.setAttribute('aria-disabled', 'true');
          if (this.atcLabelEl) this.atcLabelEl.textContent = 'Sold out';
          if (this.mobileLabelEl) this.mobileLabelEl.textContent = 'Sold out';
        }
      }

      // Refresh PC + Klarna split lines on price change.
      // Earn rate honors current tier multiplier when available (Phase 5).
      const flcEl = this.root.querySelector('[data-pdp-flc]');
      if (flcEl) {
        const PL = window.PhraseLoyalty;
        flcEl.textContent = PL ? PL.earnForPriceCents(variant.price) : Math.floor(variant.price / 10);
      }
      // Keep [data-phrase-flc-earn] wrapper's price-cents in sync so re-hydration is correct.
      const earnWrap = this.root.querySelector('[data-phrase-flc-earn]');
      if (earnWrap) earnWrap.setAttribute('data-price-cents', String(variant.price));
      const klarnaEl = this.root.querySelector('[data-pdp-klarna-split]');
      if (klarnaEl) klarnaEl.textContent = formatMoney(Math.round(variant.price / 3), moneyFormat);

      // Sync Klarna On-Site Messaging placement amount (SDK rerenders on attr change)
      const klarnaPlacement = this.root.querySelector('klarna-placement');
      if (klarnaPlacement) {
        klarnaPlacement.setAttribute('data-purchase-amount', String(variant.price));
        if (window.Klarna && window.Klarna.OnsiteMessaging && typeof window.Klarna.OnsiteMessaging.refresh === 'function') {
          window.Klarna.OnsiteMessaging.refresh();
        }
      }

      // Hide Klarna wrap entirely if the new variant drops under €30
      const klarnaWrap = this.root.querySelector('[data-pdp-klarna-wrap]');
      if (klarnaWrap) {
        if (variant.price < 3000) {
          klarnaWrap.setAttribute('hidden', '');
        } else {
          klarnaWrap.removeAttribute('hidden');
        }
      }

      // Refresh free-shipping progress with new price
      this.hydrateFreeShipping();
    }

    goToMedia(idx) {
      const track = this.root.querySelector('[data-pdp-gallery-track]');
      if (!track) return;
      const tile = track.querySelectorAll('[data-pdp-media-index]')[idx];
      if (tile) tile.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }

    bindGallery() {
      const track = this.root.querySelector('[data-pdp-gallery-track]');
      const dots = this.root.querySelectorAll('[data-pdp-dot]');
      if (!track || dots.length === 0) return;

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.goToMedia(parseInt(dot.dataset.pdpDotIndex, 10));
        });
      });

      if ('IntersectionObserver' in window) {
        const tiles = track.querySelectorAll('[data-pdp-media-index]');
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                const idx = parseInt(entry.target.dataset.pdpMediaIndex, 10);
                dots.forEach((d) => {
                  const dIdx = parseInt(d.dataset.pdpDotIndex, 10);
                  if (dIdx === idx) {
                    d.classList.add('is-active');
                    d.setAttribute('aria-selected', 'true');
                  } else {
                    d.classList.remove('is-active');
                    d.setAttribute('aria-selected', 'false');
                  }
                });
              }
            });
          },
          { root: track, threshold: [0.6] }
        );
        tiles.forEach((t) => io.observe(t));
      }
    }

    bindZoom() {
      const dialog = this.root.querySelector('[data-pdp-zoom-dialog]');
      if (!dialog || typeof dialog.showModal !== 'function') return;

      const triggers = Array.from(this.root.querySelectorAll('[data-pdp-zoom-open]'));
      if (triggers.length === 0) return;
      const sources = triggers.map((btn) => {
        const img = btn.querySelector('img');
        return {
          large: img && img.dataset.pdpZoomLarge ? img.dataset.pdpZoomLarge : (img ? img.src : ''),
          alt: img ? img.alt : ''
        };
      });

      const stage = dialog.querySelector('[data-pdp-zoom-stage]');
      const img = dialog.querySelector('[data-pdp-zoom-img]');
      const counter = dialog.querySelector('[data-pdp-zoom-counter]');
      const prevBtn = dialog.querySelector('[data-pdp-zoom-prev]');
      const nextBtn = dialog.querySelector('[data-pdp-zoom-next]');
      const closeBtn = dialog.querySelector('[data-pdp-zoom-close]');

      const state = {
        index: 0,
        scale: 1,
        tx: 0,
        ty: 0,
        startX: 0,
        startY: 0,
        startTx: 0,
        startTy: 0,
        dragging: false,
        pinching: false,
        pinchStartDist: 0,
        pinchStartScale: 1,
        swipeStartY: 0,
        swipeStartX: 0,
        swipeActive: false
      };

      const applyTransform = () => {
        img.style.transform = `translate(${state.tx}px, ${state.ty}px) scale(${state.scale})`;
        if (state.scale > 1.02) stage.classList.add('is-zoomed');
        else stage.classList.remove('is-zoomed');
      };

      const setIndex = (i) => {
        state.index = (i + sources.length) % sources.length;
        state.scale = 1; state.tx = 0; state.ty = 0;
        img.src = sources[state.index].large;
        img.alt = sources[state.index].alt;
        applyTransform();
        if (counter) counter.textContent = (state.index + 1) + ' / ' + sources.length;
        if (sources.length <= 1) {
          if (prevBtn) prevBtn.hidden = true;
          if (nextBtn) nextBtn.hidden = true;
        }
      };

      const open = (i) => {
        setIndex(i);
        if (!dialog.open) dialog.showModal();
        document.documentElement.classList.add('phrase-zoom-open');
      };
      const close = () => {
        if (dialog.open) dialog.close();
        state.scale = 1; state.tx = 0; state.ty = 0; applyTransform();
        document.documentElement.classList.remove('phrase-zoom-open');
      };

      // Desktop only: the fullscreen <dialog> zoom can leave mobile Safari unable
      // to scroll after it closes, so on touch devices we skip it (swipe + native
      // pinch-zoom cover detail viewing instead).
      var zoomFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      triggers.forEach((btn, i) => btn.addEventListener('click', (e) => {
        if (!zoomFinePointer) return;
        e.preventDefault();
        open(i);
      }));
      if (closeBtn) closeBtn.addEventListener('click', close);
      if (prevBtn) prevBtn.addEventListener('click', () => setIndex(state.index - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => setIndex(state.index + 1));

      dialog.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
      dialog.addEventListener('click', (e) => {
        const rect = stage.getBoundingClientRect();
        const insideStage = e.clientX >= rect.left && e.clientX <= rect.right
          && e.clientY >= rect.top && e.clientY <= rect.bottom;
        if (!insideStage && e.target === dialog) close();
      });

      stage.addEventListener('wheel', (e) => {
        e.preventDefault();
        const delta = -e.deltaY * 0.0024;
        const prev = state.scale;
        state.scale = Math.max(1, Math.min(4, state.scale + delta));
        if (state.scale === 1) { state.tx = 0; state.ty = 0; }
        else {
          const rect = stage.getBoundingClientRect();
          const cx = e.clientX - rect.left;
          const cy = e.clientY - rect.top;
          const k = state.scale / prev;
          state.tx = cx - (cx - state.tx) * k;
          state.ty = cy - (cy - state.ty) * k;
        }
        applyTransform();
      }, { passive: false });

      stage.addEventListener('dblclick', (e) => {
        if (state.scale > 1.02) {
          state.scale = 1; state.tx = 0; state.ty = 0;
        } else {
          state.scale = 2.2;
          const rect = stage.getBoundingClientRect();
          state.tx = -(e.clientX - rect.left) * (state.scale - 1);
          state.ty = -(e.clientY - rect.top) * (state.scale - 1);
        }
        applyTransform();
      });

      const onPointerDown = (e) => {
        if (state.scale > 1.02) {
          state.dragging = true;
          state.startX = e.clientX; state.startY = e.clientY;
          state.startTx = state.tx; state.startTy = state.ty;
          stage.classList.add('is-grabbing');
          stage.setPointerCapture && stage.setPointerCapture(e.pointerId);
        } else {
          state.swipeActive = true;
          state.swipeStartY = e.clientY;
          state.swipeStartX = e.clientX;
        }
      };
      const onPointerMove = (e) => {
        if (state.dragging) {
          state.tx = state.startTx + (e.clientX - state.startX);
          state.ty = state.startTy + (e.clientY - state.startY);
          applyTransform();
        }
      };
      const onPointerUp = (e) => {
        if (state.dragging) {
          state.dragging = false;
          stage.classList.remove('is-grabbing');
          return;
        }
        if (state.swipeActive) {
          const dy = e.clientY - state.swipeStartY;
          const dx = e.clientX - state.swipeStartX;
          if (Math.abs(dy) > 80 && Math.abs(dy) > Math.abs(dx)) { close(); }
          else if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) && sources.length > 1) {
            setIndex(state.index + (dx < 0 ? 1 : -1));
          }
          state.swipeActive = false;
        }
      };
      stage.addEventListener('pointerdown', onPointerDown);
      stage.addEventListener('pointermove', onPointerMove);
      stage.addEventListener('pointerup', onPointerUp);
      stage.addEventListener('pointercancel', onPointerUp);

      document.addEventListener('keydown', (e) => {
        if (!dialog.open) return;
        if (e.key === 'ArrowLeft' && sources.length > 1) setIndex(state.index - 1);
        else if (e.key === 'ArrowRight' && sources.length > 1) setIndex(state.index + 1);
        else if (e.key === '0') { state.scale = 1; state.tx = 0; state.ty = 0; applyTransform(); }
      });
    }

    bindMobileBar() {
      if (!this.mobileBar || !this.atcButton) return;
      // Runs on desktop + mobile so the sticky cart appears on both once the
      // main add-to-cart button scrolls out of view.

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.mobileBar.classList.remove('is-visible');
              this.mobileBar.setAttribute('aria-hidden', 'true');
            } else if (entry.boundingClientRect.top < 0) {
              this.mobileBar.classList.add('is-visible');
              this.mobileBar.setAttribute('aria-hidden', 'false');
            } else {
              this.mobileBar.classList.remove('is-visible');
              this.mobileBar.setAttribute('aria-hidden', 'true');
            }
          });
        },
        { threshold: 0 }
      );
      observer.observe(this.atcButton);
    }

    bindMobileCta() {
      if (!this.mobileCta || !this.atcButton) return;
      this.mobileCta.addEventListener('click', () => {
        // If the piece can be added (variant selected + available), add it in one
        // click and let the cart drawer open. Only bring the user up to the form
        // when the button is unavailable (sold out / selection still required).
        var blocked = this.atcButton.disabled || this.atcButton.getAttribute('aria-disabled') === 'true';
        if (blocked) {
          this.atcButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        this.atcButton.click();
      });
    }

    bindSizeGuide() {
      const trigger = this.root.querySelector('[data-pdp-size-guide]');
      const dialog = this.root.querySelector('[data-pdp-size-dialog]');
      if (!trigger || !dialog) return;

      const open = (e) => {
        if (e) e.preventDefault();
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
      };
      const close = () => {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      };

      trigger.addEventListener('click', open);

      dialog.querySelectorAll('[data-pdp-size-dialog-close]').forEach((btn) => {
        btn.addEventListener('click', close);
      });

      // Click outside the dialog body closes (native <dialog> backdrop is the dialog element itself)
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) close();
      });
    }

    hydrateDeliveryDate() {
      const row = this.root.querySelector('[data-pdp-delivery-row]');
      if (!row) return;
      const target = row.querySelector('[data-pdp-delivery-text]');
      if (!target) return;

      // Always +2 days from the current day (Sam 2026-06-01).
      const eta = new Date();
      eta.setDate(eta.getDate() + 2);
      const fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      });
      target.textContent = `Expected delivery ${fmt.format(eta)}.`;
    }

    hydrateFreeShipping() {
      const row = this.root.querySelector('[data-pdp-freeship-row]');
      if (!row) return;
      const target = row.querySelector('[data-pdp-freeship-text]');
      const threshold = parseInt(row.dataset.pdpFreeshipThreshold, 10) || 10000;
      if (!target) return;

      const variant = this.findMatchingVariant();
      const variantPriceCents = variant ? variant.price : 0;
      const thresholdMoneyFallback = formatMoney(threshold, moneyFormat);

      fetch('/cart.js', { headers: { accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((cart) => {
          const cartTotalCents = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
          const remaining = Math.max(0, threshold - cartTotalCents);
          const pct = Math.min(100, Math.round((cartTotalCents / threshold) * 100));
          row.style.setProperty('--phrase-freeship-pct', pct + '%');
          if (cartTotalCents <= 0) {
            // Empty cart: just state the threshold, no misleading "add Xeuro" based on this single item.
            target.innerHTML = `Free shipping over ${thresholdMoneyFallback}.`;
          } else if (remaining === 0) {
            target.innerHTML = `Free shipping unlocked. <strong>You qualify.</strong>`;
          } else {
            target.innerHTML = `Free shipping over ${thresholdMoneyFallback}. <strong>${formatMoney(remaining, moneyFormat)}</strong> to go.`;
          }
        })
        .catch(() => {
          // Fallback: just show the threshold note
          target.textContent = `Free shipping over ${thresholdMoneyFallback}.`;
        });
    }

    trackRecentlyViewed() {
      try {
        const handle = this.root.querySelector('[data-pdp-recently-grid]')
          ? this.root.querySelector('[data-pdp-recently-grid]').dataset.currentHandle
          : null;
        // We always track even if the rail itself is hidden, so future visits can render.
        const meta = this.root.dataset;
        const productHandle = (handle || (window.location.pathname.match(/\/products\/([^\/?#]+)/) || [])[1]);
        if (!productHandle) return;
        const KEY = 'phrase_recently_viewed';
        let arr = [];
        try { arr = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { arr = []; }
        arr = arr.filter((h) => h && h !== productHandle);
        arr.unshift(productHandle);
        if (arr.length > 12) arr = arr.slice(0, 12);
        localStorage.setItem(KEY, JSON.stringify(arr));
      } catch (e) {
        // localStorage blocked → silent
      }
    }

    renderRecentlyViewed() {
      const section = this.root.querySelector('[data-pdp-recently]');
      const grid = this.root.querySelector('[data-pdp-recently-grid]');
      if (!section || !grid) return;

      const currentHandle = grid.dataset.currentHandle;
      let handles = [];
      try {
        handles = JSON.parse(localStorage.getItem('phrase_recently_viewed') || '[]');
      } catch (e) {
        handles = [];
      }
      handles = (handles || []).filter((h) => h && h !== currentHandle).slice(0, 4);
      if (handles.length === 0) {
        section.setAttribute('hidden', '');
        return;
      }

      Promise.all(
        handles.map((h) =>
          fetch(`/products/${encodeURIComponent(h)}.js`, { headers: { accept: 'application/json' } })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        )
      ).then((products) => {
        const valid = products.filter((p) => p && p.handle);
        if (valid.length === 0) {
          section.setAttribute('hidden', '');
          return;
        }
        grid.innerHTML = valid
          .map((p) => {
            const image = p.featured_image
              ? (p.featured_image.startsWith('//') ? 'https:' + p.featured_image : p.featured_image)
              : '';
            const price = formatMoney(p.price, moneyFormat);
            const title = (p.title || '').replace(/[<>&"']/g, (c) => ({
              '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;'
            })[c]);
            return `
              <a href="/products/${encodeURIComponent(p.handle)}" class="phrase-pdp__recently-tile">
                <div class="phrase-pdp__recently-tile-media">
                  ${image ? `<img src="${image}" alt="" loading="lazy" decoding="async">` : ''}
                </div>
                <p class="phrase-pdp__recently-tile-title">${title}</p>
                <p class="phrase-pdp__recently-tile-price">${price}</p>
              </a>
            `;
          })
          .join('');
        section.removeAttribute('hidden');
      });
    }

    fetchRecommendations() {
      const grid = this.root.querySelector('[data-pdp-recs-grid]');
      if (!grid) return;
      const productId = grid.dataset.productId;
      const url = grid.dataset.url;
      const intent = grid.dataset.intent || 'related';
      const limit = grid.dataset.limit || 4;
      if (!productId || !url) return;

      const endpoint = `${url}?section_id=${this.sectionId}&product_id=${productId}&intent=${intent}&limit=${limit}`;
      fetch(endpoint)
        .then((r) => (r.ok ? r.text() : null))
        .then((html) => {
          if (!html) return;
          const tmp = document.createElement('div');
          tmp.innerHTML = html.trim();
          const replacement = tmp.querySelector('[data-pdp-recs-grid]');
          if (replacement && replacement.children.length > 0) {
            grid.innerHTML = replacement.innerHTML;
          }
        })
        .catch(() => {
          // Silent fail — server-rendered fallback stays in place
        });
    }
  }

  function init() {
    document.querySelectorAll('.phrase-pdp').forEach((root) => {
      if (root.__phrasePdpInit) return;
      root.__phrasePdpInit = true;
      new PhrasePDP(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
